// src/contexts/session-context.js
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { createHttpClient } from "../api/client";
import { SESSION_NAME } from "../constants/session";

const SessionContext = createContext(null);

function safeJsonParse(x) {
  try {
    return x ? JSON.parse(x) : null;
  } catch {
    return null;
  }
}

function getJwt() {
  const lsJwt = localStorage.getItem("jwt");
  const ckJwt = Cookies.get(SESSION_NAME);
  return lsJwt || ckJwt || null;
}

function normalizeProfileResponse(resData) {
  // รองรับทั้ง { user: {...} } และกรณี backend อาจส่ง {...} ตรงๆ
  const u = resData?.user ?? resData ?? null;
  if (!u) return { user: null, roleName: "" };

  const roleName = String(u?.role?.name ?? "").trim(); // ✅ เอา name ตามที่ต้องการ
  return { user: u, roleName };
}

export function SessionProvider({ children }) {
  const [session, setSession] = useState({
    isAuthenticated: false,
    user: null,
    roleName: "", // ✅ เพิ่ม
    loading: true,
  });

  const http = useMemo(() => createHttpClient(), []);

  // ✅ กันยิงซ้ำ
  const fetchedRef = useRef(false);
  const inFlightRef = useRef(null); // Promise
  const lastJwtRef = useRef(null);

  useEffect(() => {
    let alive = true;

    const BYPASS = String(process.env.REACT_APP_BYPASS_AUTH || "") === "true";
    if (BYPASS) {
      const mockUser = { id: 1, username: "Admin (Bypass)", role: { name: "SuperAdmin" } };
      const roleName = "SuperAdmin";

      setSession({ isAuthenticated: true, user: mockUser, roleName, loading: false });
      localStorage.setItem("USER_ID", String(mockUser.id));
      localStorage.setItem("auth_user", JSON.stringify(mockUser));
      localStorage.setItem("auth_role_name", roleName);
      return () => {
        alive = false;
      };
    }

    (async () => {
      try {
        const jwt = getJwt();
        const cachedUser = safeJsonParse(localStorage.getItem("auth_user"));
        const cachedRoleName = String(localStorage.getItem("auth_role_name") || "");

        // ไม่มี token => ไม่ login
        if (!jwt) {
          if (!alive) return;
          fetchedRef.current = false;
          inFlightRef.current = null;
          lastJwtRef.current = null;

          setSession({ isAuthenticated: false, user: null, roleName: "", loading: false });
          localStorage.removeItem("USER_ID");
          localStorage.removeItem("auth_user");
          localStorage.removeItem("auth_role_name");
          return;
        }

        // ถ้า token เปลี่ยน ให้อนุญาต fetch ใหม่ 1 ครั้ง
        if (lastJwtRef.current !== jwt) {
          lastJwtRef.current = jwt;
          fetchedRef.current = false;
          inFlightRef.current = null;
        }

        // hydrate cache ให้ UI ไม่ว่าง
        if (cachedUser?.id && alive) {
          setSession({
            isAuthenticated: true,
            user: cachedUser,
            roleName: cachedRoleName || String(cachedUser?.role?.name || ""),
            loading: true,
          });
          localStorage.setItem("USER_ID", String(cachedUser.id));
        } else if (alive) {
          setSession((s) => ({ ...s, isAuthenticated: true, loading: true }));
        }

        // ✅ ถ้าเคย fetch แล้ว ไม่ต้องยิงซ้ำ
        if (fetchedRef.current) {
          if (!alive) return;
          setSession((s) => ({ ...s, loading: false }));
          return;
        }

        // ✅ ถ้ามี request กำลังบินอยู่ ใช้อันเดิม
        if (!inFlightRef.current) {
          inFlightRef.current = http.get("/auth/profile"); // ✅ เปลี่ยนมาใช้เส้นนี้
        }

        const res = await inFlightRef.current;

        if (!alive) return;

        fetchedRef.current = true;
        inFlightRef.current = null;

        const { user, roleName } = normalizeProfileResponse(res?.data);

        if (user?.id) {
          setSession({ isAuthenticated: true, user, roleName, loading: false });
          localStorage.setItem("USER_ID", String(user.id));
          localStorage.setItem("auth_user", JSON.stringify(user));
          localStorage.setItem("auth_role_name", roleName || "");
        } else {
          setSession({ isAuthenticated: false, user: null, roleName: "", loading: false });
          localStorage.removeItem("USER_ID");
          localStorage.removeItem("auth_user");
          localStorage.removeItem("auth_role_name");
        }
      } catch (err) {
        if (!alive) return;

        fetchedRef.current = false;
        inFlightRef.current = null;

        setSession({ isAuthenticated: false, user: null, roleName: "", loading: false });
        localStorage.removeItem("USER_ID");
        localStorage.removeItem("auth_user");
        localStorage.removeItem("auth_role_name");
      }
    })();

    return () => {
      alive = false;
    };
  }, [http]);

  return <SessionContext.Provider value={[session, setSession]}>{children}</SessionContext.Provider>;
}

export function useSessionContext() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSessionContext must be used within SessionProvider");
  return ctx;
}